import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getAssets, 
  getPendingTransfers, 
  getMinistry,
  getMinistries,
  deleteAsset, 
  transferAsset, 
  confirmTransfer, 
  rejectTransfer 
} from '../services/api';
import { formatDate } from '../utils/formatters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBoxes, 
  faPlus, 
  faBarcode, 
  faTag, 
  faBuilding,
  faExchangeAlt,
  faCheckCircle,
  faTimesCircle,
  faTrash,
  faEdit,
  faSpinner,
  faArrowLeft,
  faClipboardCheck,
  faBell,
  faSearch,
  faCamera,
  faInfoCircle,
  faHistory,
  faCubes,
  faDownload,
  faCopy,
  faMapMarkerAlt,
  faMinus,
  faArrowRight,
  faLayerGroup,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import './MinistryAssets.css';

const MinistryAssets = ({ selectedYear }) => {
  const { id: ministryId } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [ministry, setMinistry] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [allMinistries, setAllMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferMinistry, setTransferMinistry] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [transferQuantity, setTransferQuantity] = useState(1);
  const [transferLoading, setTransferLoading] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Confirm/Reject loading states
  const [actionLoading, setActionLoading] = useState({});

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailAsset, setDetailAsset] = useState(null);

  useEffect(() => {
    loadData();
  }, [ministryId, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ministryRes, assetsRes, pendingRes, ministriesRes] = await Promise.all([
        getMinistry(ministryId, selectedYear),
        getAssets(ministryId),
        getPendingTransfers(ministryId),
        getMinistries(selectedYear)
      ]);
      setMinistry(ministryRes.data);
      setAssets(assetsRes.data);
      setPendingTransfers(pendingRes.data);
      setAllMinistries(ministriesRes.data.filter(m => m._id !== ministryId));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do patrimônio.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTransferModal = (asset) => {
    setTransferTarget(asset);
    setTransferMinistry('');
    setTransferNotes('');
    setTransferQuantity(asset.quantity);
    setShowTransferModal(true);
  };

  const handleTransfer = async () => {
    if (!transferMinistry) {
      toast.warn('Selecione o ministério de destino.');
      return;
    }
    if (!transferQuantity || transferQuantity < 1) {
      toast.warn('A quantidade deve ser no mínimo 1.');
      return;
    }
    if (transferQuantity > transferTarget.quantity) {
      toast.warn(`Quantidade indisponível. Este bem possui apenas ${transferTarget.quantity} unidade(s).`);
      return;
    }
    try {
      setTransferLoading(true);
      await transferAsset(transferTarget._id, {
        toMinistry: transferMinistry,
        notes: transferNotes,
        quantity: transferQuantity
      });
      const isPartial = transferQuantity < transferTarget.quantity;
      toast.success(
        isPartial
          ? `Remanejamento parcial solicitado: ${transferQuantity} de ${transferTarget.quantity} unidade(s).`
          : 'Transferência solicitada com sucesso!'
      );
      setShowTransferModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao solicitar transferência.');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleConfirmTransfer = async (assetId) => {
    try {
      setActionLoading(prev => ({ ...prev, [assetId]: 'confirming' }));
      await confirmTransfer(assetId);
      toast.success('Transferência confirmada com sucesso!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao confirmar transferência.');
    } finally {
      setActionLoading(prev => ({ ...prev, [assetId]: null }));
    }
  };

  const handleRejectTransfer = async (assetId) => {
    try {
      setActionLoading(prev => ({ ...prev, [assetId]: 'rejecting' }));
      await rejectTransfer(assetId);
      toast.info('Transferência recusada.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao recusar transferência.');
    } finally {
      setActionLoading(prev => ({ ...prev, [assetId]: null }));
    }
  };

  const handleOpenDeleteModal = (asset) => {
    setDeleteTarget(asset);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await deleteAsset(deleteTarget._id);
      toast.success('Bem removido com sucesso!');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover bem.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleShowDetail = (asset) => {
    setDetailAsset(asset);
    setShowDetailModal(true);
  };

  const filteredAssets = assets.filter(asset =>
    asset.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (asset.serialNumber && asset.serialNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (asset.location && asset.location.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleDownloadPhoto = async (url, index) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `foto-${index + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast.success('Download iniciado.');
    } catch (error) {
      console.error('Erro ao baixar foto:', error);
      toast.error('Não foi possível baixar a foto.');
    }
  };

  const handleCopyPhoto = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const pngBlob = new Blob([blob], { type: 'image/png' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlob })
      ]);
      toast.success('Foto copiada para a área de transferência.');
    } catch (error) {
      console.error('Erro ao copiar foto:', error);
      // Fallback: copy the URL
      try {
        await navigator.clipboard.writeText(url);
        toast.info('Link da foto copiado para a área de transferência.');
      } catch {
        toast.error('Não foi possível copiar a foto.');
      }
    }
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  if (loading) {
    return (
      <div className="loading">
        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
        Carregando patrimônio...
      </div>
    );
  }

  return (
    <div className="assets-page">
      <div className="container">
        {/* Header */}
        <div className="assets-header">
          <div className="assets-header-left">
            <button onClick={() => navigate(`/ministry/${ministryId}`)} className="btn-back">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h1>
                <FontAwesomeIcon icon={faBoxes} className="header-icon" />
                Patrimônio
              </h1>
              <p className="assets-subtitle">{ministry?.name}</p>
            </div>
          </div>
          <div className="assets-header-right">
            <div className="assets-stats-badge">
              <FontAwesomeIcon icon={faCubes} />
              <span>{assets.length} {assets.length === 1 ? 'bem' : 'bens'}</span>
            </div>
            <Link to={`/ministry/${ministryId}/assets/new`} className="btn btn-primary">
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
              Cadastrar Bem
            </Link>
          </div>
        </div>

        {/* Pending Transfers Alert */}
        {pendingTransfers.length > 0 && (
          <div className="pending-transfers-alert">
            <div className="alert-header">
              <FontAwesomeIcon icon={faBell} className="alert-icon" />
              <h3>Transferências Aguardando Confirmação</h3>
              <span className="pending-count">{pendingTransfers.length}</span>
            </div>
            <div className="pending-transfers-list">
              {pendingTransfers.map((asset) => (
                <div key={asset._id} className="pending-transfer-item">
                  <div className="pending-transfer-info">
                    <div className="pending-transfer-main">
                      <span className="pending-code">{asset.code}</span>
                      <span className="pending-description">{asset.description}</span>
                    </div>
                    <p className="pending-from">
                      <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '4px' }} />
                      De: <strong>{asset.ministry?.name}</strong>
                    </p>
                  </div>
                  <div className="pending-transfer-actions">
                    <button
                      onClick={() => handleConfirmTransfer(asset._id)}
                      className="btn btn-success btn-sm"
                      disabled={!!actionLoading[asset._id]}
                    >
                      {actionLoading[asset._id] === 'confirming' ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '4px' }} />
                          Confirmar
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleRejectTransfer(asset._id)}
                      className="btn btn-danger btn-sm"
                      disabled={!!actionLoading[asset._id]}
                    >
                      {actionLoading[asset._id] === 'rejecting' ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: '4px' }} />
                          Recusar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Bar */}
        {assets.length > 0 && (
          <div className="assets-search">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por descrição, código, nº de série ou localização..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        )}

        {/* Assets List */}
        {filteredAssets.length === 0 ? (
          <div className="empty-assets">
            <FontAwesomeIcon icon={faBoxes} className="empty-icon" />
            <h3>{searchTerm ? 'Nenhum bem encontrado' : 'Nenhum bem cadastrado'}</h3>
            <p>{searchTerm ? 'Tente buscar com outros termos.' : 'Comece cadastrando o primeiro bem deste ministério.'}</p>
            {!searchTerm && (
              <Link to={`/ministry/${ministryId}/assets/new`} className="btn btn-primary">
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
                Cadastrar Primeiro Bem
              </Link>
            )}
          </div>
        ) : (
          <div className="assets-grid">
            {filteredAssets.map((asset) => (
              <div key={asset._id} className={`asset-card ${asset.status === 'pending_transfer' ? 'asset-pending' : ''}`}>
                {/* Photo thumbnail */}
                <div className="asset-card-photo" onClick={() => handleShowDetail(asset)}>
                  {asset.photos && asset.photos.length > 0 ? (
                    <img src={asset.photos[0]} alt={asset.description} />
                  ) : (
                    <div className="no-photo">
                      <FontAwesomeIcon icon={faCamera} />
                    </div>
                  )}
                  {asset.status === 'pending_transfer' && (
                    <div className="asset-status-badge pending">
                      <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '4px' }} />
                      Em Transferência
                    </div>
                  )}
                </div>

                {/* Card content */}
                <div className="asset-card-body" onClick={() => handleShowDetail(asset)}>
                  <div className="asset-card-code">
                    <FontAwesomeIcon icon={faBarcode} />
                    <span>{asset.code}</span>
                  </div>
                  <h3 className="asset-card-title">{asset.description}</h3>
                  {asset.serialNumber && (
                    <div className="asset-card-serial">
                      <FontAwesomeIcon icon={faTag} />
                      <span>Etiqueta: {asset.serialNumber}</span>
                    </div>
                  )}
                  {asset.location && (
                    <div className="asset-card-location">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      <span>{asset.location}</span>
                    </div>
                  )}
                  {asset.quantity > 1 && (
                    <div className="asset-card-qty">
                      Qtd: {asset.quantity}
                    </div>
                  )}
                  {asset.value > 0 && (
                    <div className="asset-card-value">
                      {formatCurrency(asset.value)}
                    </div>
                  )}
                </div>

                {/* Card actions */}
                <div className="asset-card-actions">
                  {asset.status === 'active' && (
                    <>
                      <button
                        onClick={() => handleOpenTransferModal(asset)}
                        className="btn-asset-action btn-transfer"
                        title="Remanejar para outro ministério"
                      >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>Remanejar</span>
                      </button>
                      <Link
                        to={`/ministry/${ministryId}/assets/${asset._id}/edit`}
                        className="btn-asset-action btn-edit"
                        title="Editar bem"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      {isAdmin && (
                        <button
                          onClick={() => handleOpenDeleteModal(asset)}
                          className="btn-asset-action btn-delete"
                          title="Remover bem"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </>
                  )}
                  {asset.status === 'pending_transfer' && (
                    <span className="transfer-pending-label">
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '4px' }} />
                      Aguardando confirmação de {asset.pendingTransfer?.name}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Transfer Modal */}
        {showTransferModal && transferTarget && (
          <div className="modal-overlay" onClick={() => !transferLoading && setShowTransferModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '8px' }} />
                  Remanejar Bem
                </h2>
                <button 
                  className="modal-close" 
                  onClick={() => !transferLoading && setShowTransferModal(false)}
                  disabled={transferLoading}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="transfer-asset-info">
                  <div className="transfer-asset-code">{transferTarget.code}</div>
                  <div className="transfer-asset-desc">{transferTarget.description}</div>
                  <div className="transfer-asset-qty-badge">
                    <FontAwesomeIcon icon={faCubes} />
                    <span>{transferTarget.quantity} {transferTarget.quantity === 1 ? 'unidade disponível' : 'unidades disponíveis'}</span>
                  </div>
                </div>

                {/* Quantity selector - only shows when quantity > 1 */}
                {transferTarget.quantity > 1 && (
                  <div className="form-group">
                    <label className="form-label transfer-qty-label">
                      <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '6px' }} />
                      Quantidade a remanejar
                      <span
                        className="transfer-qty-help"
                        title="Você pode remanejar apenas parte da quantidade. O restante permanecerá no ministério atual."
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} />
                      </span>
                    </label>
                    <div className="transfer-qty-control">
                      <button
                        type="button"
                        className="transfer-qty-btn"
                        onClick={() => setTransferQuantity(prev => Math.max(1, prev - 1))}
                        disabled={transferLoading || transferQuantity <= 1}
                        title="Diminuir quantidade"
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <input
                        type="number"
                        className="transfer-qty-input"
                        value={transferQuantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (isNaN(val) || val < 1) {
                            setTransferQuantity(1);
                          } else if (val > transferTarget.quantity) {
                            setTransferQuantity(transferTarget.quantity);
                          } else {
                            setTransferQuantity(val);
                          }
                        }}
                        min={1}
                        max={transferTarget.quantity}
                        disabled={transferLoading}
                      />
                      <button
                        type="button"
                        className="transfer-qty-btn"
                        onClick={() => setTransferQuantity(prev => Math.min(transferTarget.quantity, prev + 1))}
                        disabled={transferLoading || transferQuantity >= transferTarget.quantity}
                        title="Aumentar quantidade"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>

                    {/* Transfer summary */}
                    <div className="transfer-qty-summary">
                      <div className="transfer-qty-summary-row">
                        <div className="transfer-qty-summary-item transfer-qty-going">
                          <FontAwesomeIcon icon={faArrowRight} />
                          <span><strong>{transferQuantity}</strong> {transferQuantity === 1 ? 'vai' : 'vão'} para o destino</span>
                        </div>
                        <div className="transfer-qty-summary-item transfer-qty-staying">
                          <FontAwesomeIcon icon={faBuilding} />
                          <span><strong>{transferTarget.quantity - transferQuantity}</strong> {(transferTarget.quantity - transferQuantity) === 1 ? 'permanece' : 'permanecem'} aqui</span>
                        </div>
                      </div>
                      {transferQuantity === transferTarget.quantity && (
                        <div className="transfer-qty-all-notice">
                          <FontAwesomeIcon icon={faInfoCircle} />
                          <span>Toda a quantidade será remanejada</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '6px' }} />
                    Ministério de Destino
                  </label>
                  <select
                    className="form-input"
                    value={transferMinistry}
                    onChange={(e) => setTransferMinistry(e.target.value)}
                    disabled={transferLoading}
                  >
                    <option value="">Selecione o ministério...</option>
                    {allMinistries.map((m) => (
                      <option key={m._id} value={m._id}>{m.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '6px' }} />
                    Observações (opcional)
                  </label>
                  <textarea
                    className="form-input form-textarea"
                    value={transferNotes}
                    onChange={(e) => setTransferNotes(e.target.value)}
                    placeholder="Motivo do remanejamento..."
                    rows={3}
                    disabled={transferLoading}
                  />
                </div>

                <div className="transfer-info-box">
                  <FontAwesomeIcon icon={faClipboardCheck} style={{ marginRight: '6px' }} />
                  O ministério de destino precisará confirmar o recebimento do bem.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="btn btn-secondary"
                  disabled={transferLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleTransfer}
                  className="btn btn-primary"
                  disabled={transferLoading || !transferMinistry}
                >
                  {transferLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                      Processando...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '6px' }} />
                      {transferTarget.quantity > 1 && transferQuantity < transferTarget.quantity
                        ? `Remanejar ${transferQuantity} unidade${transferQuantity > 1 ? 's' : ''}`
                        : 'Confirmar Remanejamento'
                      }
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && deleteTarget && (
          <div className="modal-overlay" onClick={() => !deleteLoading && setShowDeleteModal(false)}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                  Remover Bem
                </h2>
                <button 
                  className="modal-close" 
                  onClick={() => !deleteLoading && setShowDeleteModal(false)}
                  disabled={deleteLoading}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja remover este bem?</p>
                <div className="alert alert-danger" style={{ marginTop: '12px' }}>
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '6px' }} />
                  <div>
                    <strong>{deleteTarget.code}</strong> — {deleteTarget.description}
                    <br />
                    <small>Esta ação não pode ser desfeita.</small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn btn-secondary"
                  disabled={deleteLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-danger"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                      Removendo...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} style={{ marginRight: '6px' }} />
                      Remover
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && detailAsset && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal-content modal-detail" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
                  Detalhes do Bem
                </h2>
                <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                  &times;
                </button>
              </div>
              <div className="modal-body">
                {/* Photos gallery */}
                {detailAsset.photos && detailAsset.photos.length > 0 && (
                  <div className="detail-photos">
                    {detailAsset.photos.map((photo, idx) => (
                      <div key={idx} className="detail-photo-wrapper">
                        <a href={photo} target="_blank" rel="noopener noreferrer" className="detail-photo">
                          <img src={photo} alt={`Foto ${idx + 1}`} />
                        </a>
                        <div className="detail-photo-actions">
                          <button
                            type="button"
                            className="photo-action-btn photo-action-download"
                            onClick={() => handleDownloadPhoto(photo, idx)}
                            title="Baixar foto"
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </button>
                          <button
                            type="button"
                            className="photo-action-btn photo-action-copy"
                            onClick={() => handleCopyPhoto(photo)}
                            title="Copiar foto para a área de transferência"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faBarcode} /> Código
                    </span>
                    <span className="detail-value">{detailAsset.code}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faBoxes} /> Descrição
                    </span>
                    <span className="detail-value">{detailAsset.description}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faBuilding} /> Departamento
                    </span>
                    <span className="detail-value">{detailAsset.ministry?.name || ministry?.name}</span>
                  </div>
                  {detailAsset.location && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> Localização
                      </span>
                      <span className="detail-value">{detailAsset.location}</span>
                    </div>
                  )}
                  {detailAsset.serialNumber && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faTag} /> Nº Etiqueta
                      </span>
                      <span className="detail-value">{detailAsset.serialNumber}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faCubes} /> Quantidade
                    </span>
                    <span className="detail-value">{detailAsset.quantity}</span>
                  </div>
                  {detailAsset.acquisitionDate && (
                    <div className="detail-item">
                      <span className="detail-label">Data de Aquisição</span>
                      <span className="detail-value">{formatDate(detailAsset.acquisitionDate)}</span>
                    </div>
                  )}
                  {detailAsset.value > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Valor</span>
                      <span className="detail-value">{formatCurrency(detailAsset.value)}</span>
                    </div>
                  )}
                </div>

                {/* Transfer history */}
                {detailAsset.transferHistory && detailAsset.transferHistory.length > 0 && (
                  <div className="detail-history">
                    <h3>
                      <FontAwesomeIcon icon={faHistory} style={{ marginRight: '6px' }} />
                      Histórico de Transferências
                    </h3>
                    <div className="history-list">
                      {detailAsset.transferHistory.map((transfer, idx) => (
                        <div key={idx} className={`history-item history-${transfer.status}`}>
                          <div className="history-status">
                            <FontAwesomeIcon 
                              icon={
                                transfer.status === 'confirmed' ? faCheckCircle :
                                transfer.status === 'rejected' ? faTimesCircle :
                                faExchangeAlt
                              } 
                            />
                            <span>
                              {transfer.status === 'confirmed' && 'Confirmada'}
                              {transfer.status === 'rejected' && 'Recusada'}
                              {transfer.status === 'pending' && 'Pendente'}
                            </span>
                          </div>
                          <p className="history-detail">
                            {transfer.fromMinistry?.name || 'N/A'} → {transfer.toMinistry?.name || 'N/A'}
                          </p>
                          <p className="history-date">
                            {formatDate(transfer.requestedAt)}
                            {transfer.confirmedAt && ` • Respondido em ${formatDate(transfer.confirmedAt)}`}
                          </p>
                          {transfer.notes && (
                            <p className="history-notes">{transfer.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MinistryAssets;
